import { FormEvent, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

interface EnablePreviewModeModalProps {
  id: string;
  name: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Confirmation modal for enabling Preview Mode on a dataset.
 *
 * Preview Mode allows datasets to be shared with "preview" access level,
 * giving users visibility into metadata without exposing raw table data.
 *
 * This action is intentionally difficult to reverse (requires admin assistance)
 * to prevent accidental toggling. The modal communicates this clearly to users.
 */
const EnablePreviewModeModal = ({ id, name, open, onClose }: EnablePreviewModeModalProps) => {
  const { mutate } = useDataset();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const resp = await fetch(`${VITE_CATALOG_API}/datasets/${id}/preview/enable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!resp.ok) {
        throw Error("There was a problem enabling preview mode");
      }

      trackEvent("dataset_preview_mode_enabled", { dataset_id: id });

      // Endpoint returns 202 with no body, so we optimistically update the local state
      mutate((currentDataset) => {
        if (!currentDataset || currentDataset.id !== id) {
          return currentDataset;
        }
        return { ...currentDataset, previewEnabled: true };
      });

      onClose();
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      setError("Unable to enable preview mode");
    }

    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} aria-describedby="enable-preview-mode-description">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Enable Preview Mode</DialogTitle>
        <DialogContent id="enable-preview-mode-description">
          <Stack gap={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="body2">
              You are enabling preview mode for{" "}
              <Typography component="span" variant="body2" fontWeight={600}>
                {name}
              </Typography>
              . This allows the dataset to be shared with preview access, where users will see
              metadata only, not underlying data.
            </Typography>
            <Alert severity="info" icon={false} sx={{ py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                This setting cannot be disabled without administrator assistance.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={submitting}>
            Enable
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EnablePreviewModeModal;
